import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  OneCWebhookPanel,
  makeSelectOneCWebhookVm,
  onecWebhookAttentionRetry,
  onecWebhookDelivery,
  onecWebhookRotate,
  type OneCResourceArgs,
  type OneCWebhookActions,
  type OneCWebhookSecret,
} from '@krp/resource-onec-ui'

import { useAppSelector } from '../../app/hooks'
import type { RootState } from '../../app/types'

/** Шаг перечитывания состояния доставки — интервал сердцебиения агента рядом с 1С по умолчанию (ONEC-HOOK-AGENT.md §12). */
const DELIVERY_POLL_MS = 60_000

/** Как часто пересчитываются подписи вида «минуту назад». */
const NOW_REFRESH_MS = 15_000

/** Текущее время для подписей: обновляется само, а не берётся из часов при каждой перерисовке. */
function useNow(periodMs: number): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), periodMs)
    return () => clearInterval(timer)
  }, [periodMs])
  return now
}

/**
 * Binding экрана «Вебхук 1С» в карточке базы. Выданный секрет живёт только в состоянии этого экрана (не в store, не в кэше
 * запросов): после ответа мутация сбрасывается, а секрет исчезает при скрытии или закрытии карточки.
 */
export function OneCWebhookBinding({ resourceId }: { resourceId: string }) {
  const args = useMemo<OneCResourceArgs>(() => ({ resourceId }), [resourceId])
  // Агент рядом с 1С шлёт сердцебиение раз в минуту: состояние перечитывается с тем же шагом, пока экран открыт и виден.
  const { refetch } = onecWebhookDelivery.useQuerySubscription(args, { pollingInterval: DELIVERY_POLL_MS, skipPollingIfUnfocused: true })
  const [rotate, rotateState] = onecWebhookRotate.useMutation()
  const [retryAttention, retryState] = onecWebhookAttentionRetry.useMutation()
  const [secret, setSecret] = useState<OneCWebhookSecret | null>(null)

  const selectDelivery = useMemo(() => onecWebhookDelivery.select(args), [args])
  const delivery = useAppSelector((state: RootState) => selectDelivery(state))

  const selectVm = useMemo(() => makeSelectOneCWebhookVm(), [])
  const now = useNow(NOW_REFRESH_MS)
  const vm = selectVm({ delivery, rotate: rotateState, secret, now, retryAttention: retryState })

  const resetRotate = rotateState.reset
  const doRotate = useCallback(async () => {
    setSecret(null)
    try {
      setSecret(await rotate(args).unwrap())
    } catch {
      // Ошибка остаётся в состоянии мутации и показывается на экране с кнопкой «Повторить».
      return
    }
    // Секрет уже на экране; из кэша запросов он убирается.
    resetRotate()
  }, [args, resetRotate, rotate])

  const actions = useMemo<OneCWebhookActions>(
    () => ({
      reload: () => void refetch(),
      rotate: () => void doRotate(),
      dismissSecret: () => setSecret(null),
      // Итог и ошибка остаются в состоянии мутации и показываются на экране; состояние доставки перечитывается само (тег).
      retryAttention: () => void retryAttention(args),
    }),
    [args, doRotate, refetch, retryAttention],
  )
  return <OneCWebhookPanel vm={vm} actions={actions} />
}
